import React, { useState, useRef, useEffect } from "react";

// 큰 수치 표시용 카드
export const BigCard = ({ icon: Icon, label, value, color }) => (
  <div style={{
    flex: 1, background: "white", padding: "20px", borderRadius: "12px",
    border: "0.5px solid #d3d1c7", display: "flex", alignItems: "center", gap: "15px"
  }}>
    <div style={{ background: `${color}10`, padding: "10px", borderRadius: "8px" }}>
      <Icon size={24} color={color} />
    </div>
    <div>
      <div style={{ fontSize: "13px", color: "#73726c" }}>{label}</div>
      <div style={{ fontSize: "20px", fontWeight: "600", color: "#1a1a1a" }}>{value}</div>
    </div>
  </div>
);

// 일반 카드 박스
export const Card = ({ children, style }) => (
  <div style={{
    background: "white", borderRadius: "12px", border: "0.5px solid #d3d1c7",
    padding: "20px", ...style
  }}>
    {children}
  </div>
);

// 표 헤더
export const Th = ({ children }) => (
  <th style={{
    textAlign: "left", padding: "12px 10px", fontSize: "12px",
    color: "#73726c", borderBottom: "1px solid #eee", fontWeight: "500"
  }}>
    {children}
  </th>
);

// 표 데이터 셀
export const Td = ({ children, style }) => (
  <td style={{
    padding: "12px 10px", fontSize: "13px", color: "#1a1a1a",
    borderBottom: "1px solid #f9f9f9", ...style
  }}>
    {children}
  </td>
);

// 품목 검색 선택 (타이핑으로 필터링)
export const ItemSearchSelect = ({ itemNames = [], value, onChange, color = '#185FA5', placeholder = '품목 선택', inputStyle: extraInputStyle }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [hoverIdx, setHoverIdx] = useState(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = query.trim()
    ? itemNames.filter(n => n.includes(query.trim()))
    : itemNames;

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <input
        type="text"
        value={open ? query : (value || '')}
        placeholder={placeholder}
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => { setQuery(''); setOpen(true); }}
        style={{
          width: '100%', padding: '9px 10px', borderRadius: 8,
          border: `1px solid ${open ? color : '#d3d1c7'}`,
          fontSize: 13, boxSizing: 'border-box', background: '#fcfbf7',
          outline: 'none', ...extraInputStyle
        }}
        autoComplete="off"
      />
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
          background: 'white', border: `1px solid ${color}55`,
          borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          zIndex: 300, maxHeight: 200, overflowY: 'auto'
        }}>
          {filtered.length === 0 ? (
            <div style={{ padding: '10px 14px', fontSize: 13, color: '#aaa' }}>
              {query.trim() ? `"${query}"에 해당하는 품목 없음` : '등록된 품목 없음'}
            </div>
          ) : (
            filtered.map((name, i) => (
              <div key={name}
                onMouseDown={() => { onChange(name); setOpen(false); setQuery(''); }}
                onMouseEnter={() => setHoverIdx(i)}
                onMouseLeave={() => setHoverIdx(null)}
                style={{
                  padding: '9px 14px', fontSize: 13, cursor: 'pointer',
                  color: name === value ? color : '#333',
                  fontWeight: name === value ? 700 : 400,
                  background: hoverIdx === i ? `${color}14` : (name === value ? `${color}08` : 'white'),
                }}>
                {name}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

// 거래처 검색 선택 (타이핑으로 필터링)
export const CustomerSearchSelect = ({ customers, value, onChange, color = '#185FA5', placeholder = '거래처 선택', inputStyle: extraInputStyle }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [hoverId, setHoverId] = useState(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = query.trim()
    ? customers.filter(c => c.name.includes(query.trim()))
    : customers;

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <input
        type="text"
        value={open ? query : (value || '')}
        placeholder={placeholder}
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => { setQuery(''); setOpen(true); }}
        style={{
          width: '100%', padding: '12px', borderRadius: 8,
          border: `1px solid ${open ? color : '#d3d1c7'}`,
          fontSize: 15, boxSizing: 'border-box', background: '#fcfbf7',
          outline: 'none', ...extraInputStyle
        }}
        autoComplete="off"
      />
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
          background: 'white', border: `1px solid ${color}55`,
          borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          zIndex: 200, maxHeight: 220, overflowY: 'auto'
        }}>
          {filtered.length === 0 ? (
            <div style={{ padding: '12px 14px', fontSize: 13, color: '#aaa' }}>
              {query.trim() ? `"${query}"에 해당하는 거래처가 없습니다` : '등록된 거래처가 없습니다'}
            </div>
          ) : (
            filtered.map(c => (
              <div key={c.id}
                onMouseDown={() => { onChange(c.name); setOpen(false); setQuery(''); }}
                onMouseEnter={() => setHoverId(c.id)}
                onMouseLeave={() => setHoverId(null)}
                style={{
                  padding: '10px 14px', fontSize: 13, cursor: 'pointer',
                  color: c.name === value ? color : '#333',
                  fontWeight: c.name === value ? 700 : 400,
                  background: hoverId === c.id ? `${color}14` : (c.name === value ? `${color}08` : 'white'),
                }}>
                {c.name}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};